import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { KeyRound, Lock, ShieldCheck, AlertTriangle, Mail, Bell, Send } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const [alpaca, setAlpaca] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("paper");
  const [keyId, setKeyId] = useState("");
  const [secret, setSecret] = useState("");
  const [saving, setSaving] = useState(false);

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  const [newEmail, setNewEmail] = useState("");
  const [emailPw, setEmailPw] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);

  const [notifPrefs, setNotifPrefs] = useState(null);
  const [savingNotif, setSavingNotif] = useState(null); // 'trade' | 'eod' | null
  const [sendingTest, setSendingTest] = useState(null); // 'trade' | 'eod' | null

  const load = async () => {
    try {
      const [r, n] = await Promise.all([
        api.get("/settings/alpaca"),
        api.get("/settings/notifications"),
      ]);
      setAlpaca(r.data);
      setMode(r.data.mode);
      setNotifPrefs(n.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveKeys = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/settings/alpaca", { mode, key_id: keyId, secret_key: secret });
      if (r.data.ok) {
        toast.success(`Saved & verified — Alpaca account ${r.data.account_status}`);
      } else {
        toast.error(r.data.error || "Saved but verification failed");
      }
      setKeyId("");
      setSecret("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save keys");
    } finally {
      setSaving(false);
    }
  };

  const switchMode = async (newMode) => {
    try {
      await api.post("/settings/mode", { mode: newMode });
      toast.success(`Switched to ${newMode.toUpperCase()} mode`);
      load();
    } catch (err) {
      toast.error("Could not switch mode");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) {
      toast.error("New password and confirmation don't match");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      toast.success("Password updated. Keep it safe.");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change password");
    } finally {
      setPwSaving(false);
    }
  };

  const toggleNotif = async (field) => {
    if (!notifPrefs) return;
    const next = !notifPrefs[field];
    setSavingNotif(field === "email_alerts_enabled" ? "trade" : "eod");
    try {
      const r = await api.patch("/settings/notifications", { [field]: next });
      setNotifPrefs(r.data);
      toast.success(`${field === "email_alerts_enabled" ? "Trade alerts" : "EOD P&L"} ${next ? "ENABLED" : "DISABLED"}`);
    } catch (err) {
      toast.error("Could not update preference");
    } finally {
      setSavingNotif(null);
    }
  };

  const sendTestEmail = async (kind) => {
    setSendingTest(kind);
    try {
      const r = await api.post("/settings/notifications/test", { kind });
      if (r.data.ok) {
        toast.success(`Test ${kind === "trade" ? "trade alert" : "EOD P&L"} sent to ${r.data.sent_to}`);
      } else {
        toast.error(r.data.error || "Email could not be sent", { duration: 8000 });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTest(null);
    }
  };

  const changeEmail = async (e) => {
    e.preventDefault();
    setEmailSaving(true);
    try {
      const r = await api.post("/auth/change-email", { new_email: newEmail, current_password: emailPw });
      // The old JWT is now invalid (sub mismatch). Persist the new token and reload.
      localStorage.setItem("token", r.data.access_token);
      toast.success(`Email updated to ${r.data.email}. Reloading...`);
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change email");
    } finally {
      setEmailSaving(false);
    }
  };

  if (loading) return <div className="p-6 mono text-xs text-[#71717A]">LOADING SETTINGS...</div>;

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="settings-page">
      <div>
        <div className="mono text-[10px] tracking-[0.22em] text-[#71717A]">ACCOUNT · INTEGRATIONS · SECURITY</div>
        <h1 className="text-4xl font-black mt-2">Settings</h1>
        <p className="text-[#A1A1AA] text-sm mt-1">
          Signed in as <span className="mono text-white">{user?.email}</span>
        </p>
      </div>

      {/* Alpaca Keys */}
      <div className="panel" data-testid="alpaca-settings-panel">
        <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between">
          <div className="flex items-center gap-2 mono text-[11px] tracking-[0.22em]">
            <KeyRound size={13} /> ALPACA API KEYS
          </div>
          <div className="flex items-center gap-2">
            <span className="mono text-[10px] text-[#71717A]">ACTIVE MODE</span>
            <div className="flex">
              {["paper", "live"].map((m) => (
                <button
                  key={m}
                  onClick={() => switchMode(m)}
                  data-testid={`mode-${m}-btn`}
                  className={`px-3 py-1 mono text-[10px] tracking-widest font-bold border ${
                    alpaca.mode === m
                      ? (m === "paper" ? "bg-[#00E676] text-black border-[#00E676]" : "bg-[#FF3B30] text-white border-[#FF3B30]")
                      : "bg-[#141415] text-[#A1A1AA] border-[#27272A] hover:border-[#52525B]"
                  }`}
                >
                  {m.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-px bg-[#27272A] border border-[#27272A]">
            <div className="bg-[#141415] p-3">
              <div className="mono text-[10px] tracking-widest text-[#71717A]">PAPER KEY</div>
              <div className={`mono text-sm mt-1 ${alpaca.paper_configured ? "gain" : "loss"}`}>
                {alpaca.paper_configured ? (alpaca.paper_key_id_masked || "CONFIGURED") : "NOT SET"}
              </div>
            </div>
            <div className="bg-[#141415] p-3">
              <div className="mono text-[10px] tracking-widest text-[#71717A]">LIVE KEY</div>
              <div className={`mono text-sm mt-1 ${alpaca.live_configured ? "gain" : "loss"}`}>
                {alpaca.live_configured ? (alpaca.live_key_id_masked || "CONFIGURED") : "NOT SET"}
              </div>
            </div>
          </div>

          <div className="border border-[#27272A] p-4 space-y-3">
            <div className="mono text-[10px] tracking-widest text-[#71717A]">UPDATE KEYS</div>
            <div className="flex items-center gap-2">
              {["paper", "live"].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  data-testid={`update-mode-${m}-btn`}
                  className={`px-3 py-1.5 mono text-[10px] tracking-widest border ${
                    mode === m
                      ? "bg-white text-black border-white"
                      : "bg-[#141415] text-[#A1A1AA] border-[#27272A]"
                  }`}
                >
                  {m === "paper" ? "📝 PAPER" : "💵 LIVE"}
                </button>
              ))}
            </div>
            <form onSubmit={saveKeys} className="space-y-3">
              <div>
                <label className="mono text-[10px] tracking-widest text-[#71717A]">APCA-API-KEY-ID</label>
                <input
                  value={keyId}
                  onChange={(e) => setKeyId(e.target.value)}
                  data-testid="alpaca-key-id-input"
                  required
                  placeholder="PKXXXX... (paper) or AKXXXX... (live)"
                  className="mt-1 w-full bg-[#09090B] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm"
                />
              </div>
              <div>
                <label className="mono text-[10px] tracking-widest text-[#71717A]">APCA-API-SECRET-KEY</label>
                <input
                  type="password"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  data-testid="alpaca-secret-input"
                  required
                  placeholder="••••••••••••••••••••"
                  className="mt-1 w-full bg-[#09090B] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm"
                />
              </div>
              <div className="text-[#A1A1AA] text-xs flex items-start gap-2">
                <ShieldCheck size={14} className="text-[#00E676] mt-0.5 shrink-0" />
                <span>
                  Keys are stored server-side and never exposed to the browser. Get yours at{" "}
                  <a href="https://app.alpaca.markets/" target="_blank" rel="noreferrer" className="underline text-white">
                    app.alpaca.markets
                  </a>
                  {" "}→ {mode === "paper" ? "Paper Trading" : "Live Trading"} → API Keys
                </span>
              </div>
              <button
                type="submit"
                disabled={saving}
                data-testid="save-keys-btn"
                className="w-full bg-[#00E676] text-black py-2.5 mono text-xs tracking-widest font-bold hover:bg-[#00C964] disabled:opacity-50"
              >
                {saving ? "VERIFYING WITH ALPACA..." : `SAVE ${mode.toUpperCase()} KEYS →`}
              </button>
            </form>
          </div>

          {alpaca.mode === "live" && (
            <div className="border border-[#FF3B30]/50 bg-[#FF3B30]/5 p-3 flex items-start gap-2">
              <AlertTriangle size={14} className="text-[#FF3B30] mt-0.5 shrink-0" />
              <div className="text-xs text-[#FF3B30]">
                LIVE MODE ACTIVE — Orders placed by the MACD bot will execute with REAL money on your live Alpaca account.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Email Notifications */}
      <div className="panel" data-testid="notifications-panel">
        <div className="px-4 py-3 border-b border-[#27272A] flex items-center gap-2 mono text-[11px] tracking-[0.22em]">
          <Bell size={13} /> EMAIL NOTIFICATIONS
        </div>
        <div className="p-4 space-y-4">
          <div className="mono text-[10px] tracking-widest text-[#71717A]">
            DELIVERED TO <span className="text-white">{notifPrefs?.recipient_email || user?.email}</span> · POWERED BY RESEND
          </div>

          {/* Trade Alerts */}
          <div className="border border-[#27272A] bg-[#141415] p-3 flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="text-white text-sm font-bold">Trade Alerts</div>
              <div className="text-[#A1A1AA] text-xs mt-1">
                Get an email the moment the bot places a BUY, SELL, SHORT, COVER, or hits a stop/take-profit.
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => sendTestEmail("trade")}
                disabled={sendingTest === "trade"}
                data-testid="send-test-trade-btn"
                className="px-2.5 py-1.5 mono text-[10px] tracking-widest border border-[#27272A] text-[#A1A1AA] hover:border-[#52525B] hover:text-white disabled:opacity-50 flex items-center gap-1.5"
              >
                <Send size={11} />
                {sendingTest === "trade" ? "SENDING..." : "TEST"}
              </button>
              <button
                type="button"
                onClick={() => toggleNotif("email_alerts_enabled")}
                disabled={savingNotif === "trade"}
                data-testid="toggle-trade-alerts"
                aria-pressed={!!notifPrefs?.email_alerts_enabled}
                className={`px-3 py-1.5 mono text-[10px] tracking-widest font-bold border w-[72px] ${
                  notifPrefs?.email_alerts_enabled
                    ? "bg-[#00E676] text-black border-[#00E676]"
                    : "bg-[#141415] text-[#A1A1AA] border-[#27272A] hover:border-[#52525B]"
                }`}
              >
                {notifPrefs?.email_alerts_enabled ? "ON" : "OFF"}
              </button>
            </div>
          </div>

          {/* EOD P&L */}
          <div className="border border-[#27272A] bg-[#141415] p-3 flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="text-white text-sm font-bold">End-of-Day P&amp;L Report</div>
              <div className="text-[#A1A1AA] text-xs mt-1">
                Daily summary at <span className="mono text-white">21:30 UTC</span> (≈ 4:30 PM ET, just after US close):
                equity change, open positions, today&rsquo;s orders, and bot signals.
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => sendTestEmail("eod")}
                disabled={sendingTest === "eod"}
                data-testid="send-test-eod-btn"
                className="px-2.5 py-1.5 mono text-[10px] tracking-widest border border-[#27272A] text-[#A1A1AA] hover:border-[#52525B] hover:text-white disabled:opacity-50 flex items-center gap-1.5"
              >
                <Send size={11} />
                {sendingTest === "eod" ? "SENDING..." : "TEST"}
              </button>
              <button
                type="button"
                onClick={() => toggleNotif("eod_pnl_enabled")}
                disabled={savingNotif === "eod"}
                data-testid="toggle-eod-pnl"
                aria-pressed={!!notifPrefs?.eod_pnl_enabled}
                className={`px-3 py-1.5 mono text-[10px] tracking-widest font-bold border w-[72px] ${
                  notifPrefs?.eod_pnl_enabled
                    ? "bg-[#00E676] text-black border-[#00E676]"
                    : "bg-[#141415] text-[#A1A1AA] border-[#27272A] hover:border-[#52525B]"
                }`}
              >
                {notifPrefs?.eod_pnl_enabled ? "ON" : "OFF"}
              </button>
            </div>
          </div>

          <div className="text-[#71717A] text-[11px] flex items-start gap-2">
            <ShieldCheck size={12} className="text-[#00E676] mt-0.5 shrink-0" />
            <span>Emails are sent from <span className="mono text-white">onboarding@resend.dev</span>. Add it to your safe-sender list to avoid spam filtering.</span>
          </div>
        </div>
      </div>

      {/* Change Email */}
      <div className="panel" data-testid="email-panel">
        <div className="px-4 py-3 border-b border-[#27272A] flex items-center gap-2 mono text-[11px] tracking-[0.22em]">
          <Mail size={13} /> CHANGE LOGIN EMAIL
        </div>
        <form onSubmit={changeEmail} className="p-4 space-y-3 max-w-md">
          <div className="mono text-[10px] text-[#71717A] tracking-widest">
            CURRENT: <span className="text-white">{user?.email}</span>
          </div>
          <div>
            <label className="mono text-[10px] tracking-widest text-[#71717A]">NEW EMAIL</label>
            <input
              type="email"
              required
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              data-testid="new-email-input"
              placeholder="you@yourdomain.com"
              className="mt-1 w-full bg-[#09090B] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm"
            />
          </div>
          <div>
            <label className="mono text-[10px] tracking-widest text-[#71717A]">CONFIRM CURRENT PASSWORD</label>
            <input
              type="password"
              required
              value={emailPw}
              onChange={(e) => setEmailPw(e.target.value)}
              data-testid="email-current-pw-input"
              className="mt-1 w-full bg-[#09090B] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={emailSaving}
            data-testid="change-email-btn"
            className="bg-white text-black px-6 py-2.5 mono text-xs tracking-widest font-bold hover:bg-[#D4D4D8] disabled:opacity-50"
          >
            {emailSaving ? "UPDATING..." : "CHANGE EMAIL →"}
          </button>
        </form>
      </div>

      {/* Password change */}
      <div className="panel" data-testid="password-panel">
        <div className="px-4 py-3 border-b border-[#27272A] flex items-center gap-2 mono text-[11px] tracking-[0.22em]">
          <Lock size={13} /> CHANGE PASSWORD
        </div>
        <form onSubmit={changePassword} className="p-4 space-y-3 max-w-md">
          <div>
            <label className="mono text-[10px] tracking-widest text-[#71717A]">CURRENT PASSWORD</label>
            <input
              type="password"
              required
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              data-testid="current-pw-input"
              className="mt-1 w-full bg-[#09090B] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm"
            />
          </div>
          <div>
            <label className="mono text-[10px] tracking-widest text-[#71717A]">NEW PASSWORD (MIN 6)</label>
            <input
              type="password"
              required
              minLength={6}
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              data-testid="new-pw-input"
              className="mt-1 w-full bg-[#09090B] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm"
            />
          </div>
          <div>
            <label className="mono text-[10px] tracking-widest text-[#71717A]">CONFIRM NEW PASSWORD</label>
            <input
              type="password"
              required
              minLength={6}
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              data-testid="confirm-pw-input"
              className="mt-1 w-full bg-[#09090B] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={pwSaving}
            data-testid="change-pw-btn"
            className="bg-white text-black px-6 py-2.5 mono text-xs tracking-widest font-bold hover:bg-[#D4D4D8] disabled:opacity-50"
          >
            {pwSaving ? "UPDATING..." : "CHANGE PASSWORD →"}
          </button>
        </form>
      </div>

      {/* Danger zone: reset trade data */}
      <div className="panel p-6 border-[#FF3B30]/40 mt-6" data-testid="danger-zone">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={16} className="text-[#FF3B30]" />
          <h2 className="mono text-sm tracking-[0.22em] text-[#FF3B30]">DANGER ZONE</h2>
        </div>
        <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">
          Wipes all closed trades, signals, positions, backtest jobs, and optimizer history
          for your account. Your <span className="text-white">strategy settings and watchlist are preserved</span>.
          Useful when switching to a fresh Alpaca paper account so old data doesn&apos;t muddy new stats.
        </p>
        <button
          type="button"
          onClick={async () => {
            if (!window.confirm("Reset ALL trade history, signals, and jobs? Cannot be undone.\n\nStrategy settings and watchlist will be preserved.")) return;
            try {
              const r = await api.post("/strategy/reset-trade-data");
              const totals = Object.values(r.data.deleted || {}).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
              toast.success(`Reset complete — cleared ${totals} records across ${Object.keys(r.data.deleted || {}).length} collections`);
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Reset failed");
            }
          }}
          data-testid="reset-trade-data-btn"
          className="border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white px-5 py-2 mono text-xs tracking-widest font-bold"
        >
          🚨 RESET TRADE DATA
        </button>
      </div>
    </div>
  );
}
